import { useState, useEffect } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useLanguage } from "../../context/LanguageContext";
import BtnPrimaryL from "../Buttons/BtnPrimaryL";
import Toast from "../Elements/Toast";
import PropTypes from "prop-types";
import RevenueCatService from "../../services/revenuecat";
import { Capacitor } from "@capacitor/core";

const NativeSubscriptionPlan = ({ hasActiveSubscription }) => {
  const { copy } = useLanguage();
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [toast, setToast] = useState({ message: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [offerings, setOfferings] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);

  const isNative = Capacitor.isNativePlatform();

  const features = [
    copy.pricing.features.unlimited_goals,
    copy.pricing.features.progress_dashboard,
    copy.pricing.features.daily_reminders,
    "Monthly finance tracker",
    copy.pricing.features.vision_board,
    copy.pricing.features.wishlist,
    copy.pricing.features.journaling,
    copy.pricing.features.calendar,
    copy.pricing.features.personalised_focus,
  ];

  // Fetch offerings from RevenueCat on native platforms
  useEffect(() => {
    const fetchOfferings = async () => {
      if (!isNative) return;

      try {
        setLoading(true);
        const result = await RevenueCatService.getOfferings();
        setOfferings(result.offerings);

        // Also fetch customer info to check subscription status
        const customer = await RevenueCatService.getCustomerInfo();
        setCustomerInfo(customer.customerInfo);
      } catch (error) {
        console.error("Error fetching offerings:", error);
        setToast({ message: "Failed to load subscription options", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchOfferings();
  }, [isNative]);

  // Get current offering and packages
  const currentOffering = offerings?.current;
  const monthlyPackage = currentOffering?.availablePackages?.find(
    (pkg) => pkg.identifier === "$rc_monthly" ||
             pkg.identifier === "monthly" ||
             pkg.product?.identifier === "niumi_monthly"
  );
  const annualPackage = currentOffering?.availablePackages?.find(
    (pkg) => pkg.identifier === "$rc_annual" ||
             pkg.identifier === "annual" ||
             pkg.product?.identifier === "niumi_yearly"
  );

  const selectedPackage = billingPeriod === "monthly" ? monthlyPackage : annualPackage;

  // Get pricing information
  const getPrice = (pkg) => {
    if (!pkg) return { price: "€5.99", interval: "month" };
    return {
      price: pkg.product.priceString,
      interval: pkg.packageType === "MONTHLY" ? "month" : "year",
    };
  };

  const monthlyPrice = getPrice(monthlyPackage);
  const yearlyPrice = getPrice(annualPackage);
  const currentPrice = billingPeriod === "monthly" ? monthlyPrice : yearlyPrice;

  const handleSubscribe = async () => {
    if (!isNative) {
      setToast({ message: "Native subscriptions only available on mobile app", type: "error" });
      return;
    }

    if (!selectedPackage) {
      setToast({ message: "Selected package not available", type: "error" });
      return;
    }

    try {
      setLoading(true);
      const result = await RevenueCatService.purchasePackage(selectedPackage);

      // Check if purchase was successful
      if (result.customerInfo.entitlements.active["Single Purchase"]) {
        setToast({
          message: "Subscription activated successfully!",
          type: "success",
        });
        setCustomerInfo(result.customerInfo);
      }
    } catch (error) {
      console.error("Error purchasing package:", error);

      // Handle user cancellation
      if (error.code === "PURCHASE_CANCELLED") {
        // Don't show error for user cancellation
        return;
      }

      setToast({
        message: error.message || "Failed to complete purchase",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      const result = await RevenueCatService.restorePurchases();

      if (result.customerInfo.entitlements.active["Single Purchase"]) {
        setToast({
          message: "Purchases restored successfully!",
          type: "success",
        });
        setCustomerInfo(result.customerInfo);
      } else {
        setToast({
          message: "No purchases found to restore",
          type: "info",
        });
      }
    } catch (error) {
      console.error("Error restoring purchases:", error);
      setToast({
        message: "Failed to restore purchases",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if user has active subscription
  const hasActiveNativeSubscription =
    customerInfo?.entitlements?.active?.["Single Purchase"] !== undefined;

  return (
    <div className="px-8 py-10 rounded-lg bg-white card-base-border flex flex-col">
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
        />
      )}

      {/* Title */}
      <div className="text-center pb-8">
        <h2 className="text-2xl sm:text-3xl font-bold">Unlock your full potential</h2>
        <p className="text-sm text-gray-600 mt-1">Start achieving your goals today</p>
      </div>

      {/* Billing Period Toggle */}
      <div className="flex justify-center pb-2">
        <div className="flex flex-row card-base-border rounded-md w-fit">
          <div
            className={`py-2 px-4 cursor-pointer text-sm ${
              billingPeriod === "monthly" ? "bg-accent-light" : "bg-transparent"
            }`}
            onClick={() => setBillingPeriod("monthly")}
          >
            Monthly
          </div>
          <div
            className={`py-2 px-4 cursor-pointer text-sm ${
              billingPeriod === "yearly" ? "bg-accent-light" : "bg-transparent"
            }`}
            onClick={() => setBillingPeriod("yearly")}
          >
            Yearly
          </div>
        </div>
      </div>

      <div className="card-base-border rounded-lg p-6 mb-6 flex flex-col gap-4">
        {loading && !offerings ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-2">
                <p className="text-4xl font-bold">{currentPrice.price}</p>
                <p className="text-gray-600">/{currentPrice.interval}</p>
              </div>
              {billingPeriod === "yearly" && (
                <p className="mt-1 text-sm text-green-600 font-medium">
                  2 months free! Billed annually
                </p>
              )}
              {billingPeriod === "monthly" && (
                <p className="mt-1 text-sm text-gray-500">Billed monthly</p>
              )}
            </div>

            <ul className="space-y-4 md:pr-10 md:pl-10 mb-10">
              {features.map((feature, index) => (
                <li key={index} className="flex gap-2 !mb-2 items-center">
                  <FaCheckCircle className="text-primary mr-2" size={15} />
                  <p className="md:max-w-70 w-full">{feature}</p>
                </li>
              ))}
            </ul>

            {!hasActiveSubscription && !hasActiveNativeSubscription && (
              <div className="flex flex-col gap-4 items-center min-h-[60px] justify-center">
                <BtnPrimaryL
                  buttonCTA={copy.pricing.subscribe_cta || "Subscribe Now"}
                  onClickLink={handleSubscribe}
                  className="text-center"
                  disabled={loading}
                />
                {isNative && (
                  <button
                    onClick={handleRestore}
                    disabled={loading}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Restore Purchases
                  </button>
                )}
              </div>
            )}

            {(hasActiveSubscription || hasActiveNativeSubscription) && (
              <div className="text-center py-4">
                <p className="text-green-600 font-semibold">
                  ✓ You have an active subscription
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

NativeSubscriptionPlan.propTypes = {
  hasActiveSubscription: PropTypes.bool,
};

export default NativeSubscriptionPlan;
